/* AUSTRALIA Visual Grammar v112.0 - finite decorative state, no commerce ownership. */
(() => {
  'use strict';

  if (window.__auVisualGrammarBooted) return;
  window.__auVisualGrammarBooted = true;
  window.__avTraceSystemBooted = true;

  const ROOT_SELECTOR = '[data-av-trace]';
  const HORIZON_SELECTOR = '[data-au-horizon]';
  const OBSESSION_SELECTOR = '[data-av-product-obsession]';
  const PROOF_METHOD_SELECTOR = '[data-av-proof-method]';
  const CART_TARGET_SELECTOR = '[data-cart-drawer-toggle], [data-cart-toggle], [href="/cart"], .header__cart, .cart-count-bubble';
  const PRODUCT_SUBMIT_SELECTOR = 'form[action*="/cart/add"] [type="submit"], [data-product-form] [type="submit"], product-form [type="submit"]';
  const SUCCESS_EVENTS = ['theme:product:add', 'theme:product:added', 'av:cart:add'];
  const ERROR_EVENTS = ['theme:product:add-error', 'theme:cart:error'];
  const BEGIN_EVENTS = ['theme:cart:add'];
  const VARIANT_EVENTS = ['variant:change', 'product:variant-change', 'theme:variant:change'];

  class AVTraceSystem {
    constructor() {
      this.roots = new Set();
      this.stateTimers = new WeakMap();
      this.viewed = new WeakSet();
      this.v112Viewed = new WeakSet();
      this.pendingButton = null;
      this.lastSuccessAt = 0;
      this.routeOverlay = null;
      this.arrivalObserver = null;
      this.galleryObserver = null;
      this.engagementObserver = null;
      this.listenersBound = false;
      this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.handleDocumentEvent = this.handleDocumentEvent.bind(this);
      this.handleSubmit = this.handleSubmit.bind(this);
      this.handleVisibility = this.handleVisibility.bind(this);
      this.handleSectionLoad = this.handleSectionLoad.bind(this);
      this.handleSectionUnload = this.handleSectionUnload.bind(this);
      this.handleBlockSelect = this.handleBlockSelect.bind(this);
      this.handleV112Click = this.handleV112Click.bind(this);
    }

    isDisabledByViewport() {
      const threshold = Number(document.body?.dataset.avTraceDisableBelowWidth || 0);
      return threshold > 0 && window.innerWidth < threshold;
    }

    prefersReducedMotion() {
      return this.reducedMotionQuery.matches || document.body?.dataset.avTraceMobileMotion === 'none' && window.innerWidth < 750;
    }

    horizonWithin(root) {
      if (!(root instanceof Element)) return null;
      return root.matches(HORIZON_SELECTOR) ? root : root.querySelector(HORIZON_SELECTOR);
    }

    horizonMotionAllowed(root) {
      if (!this.horizonWithin(root)) return true;
      const body = document.body;
      return body?.dataset.avV112 === 'true'
        && body.dataset.avV112Horizon === 'true'
        && body.dataset.avV112HorizonMotion === 'true'
        && body.dataset.motionEnabled !== 'false'
        && body.dataset.motionIntensity !== 'off'
        && !this.prefersReducedMotion();
    }

    v112CustomConfigured() {
      return document.body?.dataset.avV112 === 'true'
        && document.body.dataset.avCustomEvents === 'true'
        && window.Shopify?.designMode !== true;
    }

    v112CustomAllowed() {
      return this.v112CustomConfigured()
        && window.AVAnalytics?.consentAllowed?.() === true
        && typeof window.AVAnalytics?.custom === 'function';
    }

    engagementNodesWithin(scope = document) {
      const selector = `${OBSESSION_SELECTOR}, ${PROOF_METHOD_SELECTOR}`;
      const found = [];
      if (scope instanceof Element && scope.matches(selector)) found.push(scope);
      if (scope.querySelectorAll) found.push(...scope.querySelectorAll(selector));
      return [...new Set(found)];
    }

    v112Payload(node, overrides = {}) {
      const element = node instanceof Element ? node : null;
      const section = element?.closest('[data-av-section-id], [data-section-id]') || element;
      const horizon = element?.matches(HORIZON_SELECTOR)
        ? element
        : element?.querySelector(HORIZON_SELECTOR) || element?.closest('[data-av-trace]')?.querySelector(HORIZON_SELECTOR);
      const product = element?.closest('[data-av-product-id], [data-product-id], [data-item-id]')
        || element?.querySelector?.('[data-av-product-id], [data-product-id], [data-item-id]');
      const worldScope = element?.closest('[data-au-world], [data-av-world], [data-category-world]');
      return {
        source: location.pathname,
        section_id: section?.dataset.avSectionId || section?.dataset.sectionId || horizon?.dataset.avSectionId || '',
        world: element?.dataset.auWorld || element?.dataset.avWorld || worldScope?.dataset.auWorld || worldScope?.dataset.avWorld || worldScope?.dataset.categoryWorld || document.body?.dataset.avWorld || 'neutral',
        variant: element?.dataset.avVariant || element?.dataset.auHorizonVariant || horizon?.dataset.auHorizonVariant || 'default',
        position: element?.dataset.avPosition || element?.dataset.auHorizonPosition || horizon?.dataset.auHorizonPosition || '',
        product_id: element?.dataset.avProductId || element?.dataset.productId || product?.dataset.avProductId || product?.dataset.productId || product?.dataset.itemId || '',
        ...overrides
      };
    }

    emitV112(name, node, overrides = {}, ttl = 30000) {
      if (!this.v112CustomAllowed()) return false;
      const detail = this.v112Payload(node, overrides);
      return window.AVAnalytics.custom(name, detail, {
        dedupeKey: [name, detail.source, detail.section_id, detail.world, detail.variant, detail.position, detail.product_id].join(':'),
        ttl
      });
    }

    tryObsessionView(node) {
      if (!(node instanceof Element) || this.v112Viewed.has(node)) return;
      if (!this.emitV112('australia:product_obsession_view', node, {
        variant: node.dataset.avVariant || 'obsession'
      })) return;
      this.v112Viewed.add(node);
      node.dataset.avV112Observed = 'true';
      this.engagementObserver?.unobserve(node);
    }

    observeV112Engagement(scope = document) {
      if (!this.v112CustomConfigured()) return;
      const nodes = this.engagementNodesWithin(scope).filter((node) => node.matches(OBSESSION_SELECTOR) && !this.v112Viewed.has(node));
      if (!nodes.length) return;
      if (!('IntersectionObserver' in window)) {
        nodes.forEach((node) => this.tryObsessionView(node));
        return;
      }
      this.engagementObserver ||= new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) this.tryObsessionView(entry.target);
        });
      }, { threshold: .42, rootMargin: '0px 0px -8% 0px' });
      nodes.forEach((node) => this.engagementObserver.observe(node));
    }

    rootsWithin(scope = document) {
      const found = [];
      if (scope instanceof Element && scope.matches(ROOT_SELECTOR)) found.push(scope);
      if (scope.querySelectorAll) found.push(...scope.querySelectorAll(ROOT_SELECTOR));
      return [...new Set(found)];
    }

    init(scope = document) {
      this.bindEvents();
      this.observeV112Engagement(scope);
      this.rootsWithin(scope).forEach((root) => {
        if (this.roots.has(root)) return;
        this.roots.add(root);
        root.dataset.avTraceEnhanced = 'true';
        if (this.isDisabledByViewport()) {
          if (root.matches('[data-av-trace]')) root.hidden = true;
          else root.querySelectorAll('[data-av-trace]').forEach((trace) => { trace.hidden = true; });
          return;
        }
        if (root.matches('[data-av-trace]')) root.hidden = false;
        else root.querySelectorAll('[data-av-trace]').forEach((trace) => { trace.hidden = false; });
        this.syncTrack(root);
        if (this.prefersReducedMotion() || !this.horizonMotionAllowed(root)) {
          this.setState(root, 'arrived');
          this.emitView(root);
          return;
        }
        this.setState(root, root.dataset.avTraceState || 'resting');
        this.observeArrival(root);
        if (root.dataset.avTraceSurface === 'pdp-gallery') this.observeGallery(root);
      });
      return this;
    }

    destroy(scope = document) {
      this.engagementNodesWithin(scope).forEach((node) => this.engagementObserver?.unobserve(node));
      this.rootsWithin(scope).forEach((root) => {
        const timer = this.stateTimers.get(root);
        if (timer) window.clearTimeout(timer);
        this.arrivalObserver?.unobserve(root);
        this.galleryObserver?.unobserve(root);
        root.removeAttribute('data-av-trace-enhanced');
        root.removeAttribute('data-av-trace-paused');
        this.roots.delete(root);
      });
      if (scope === document) {
        this.arrivalObserver?.disconnect();
        this.galleryObserver?.disconnect();
        this.engagementObserver?.disconnect();
        this.arrivalObserver = null;
        this.galleryObserver = null;
        this.engagementObserver = null;
        this.removeRouteOverlay();
      }
      return this;
    }

    resolveRoot(node) {
      if (!(node instanceof Element)) return null;
      return node.matches(ROOT_SELECTOR) ? node : node.closest(ROOT_SELECTOR) || node.closest('[data-av-trace-host], .av-trace-host')?.querySelector(ROOT_SELECTOR) || null;
    }

    observeArrival(root) {
      if (!('IntersectionObserver' in window)) {
        this.setState(root, 'arrived');
        this.emitView(root);
        return;
      }
      if (!this.arrivalObserver) {
        this.arrivalObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            this.setState(entry.target, 'arriving', this.routeDuration(entry.target));
            this.emitView(entry.target);
            this.arrivalObserver.unobserve(entry.target);
          });
        }, { rootMargin: '8% 0px', threshold: 0.14 });
      }
      this.arrivalObserver.observe(root);
    }

    observeGallery(root) {
      if (!('IntersectionObserver' in window)) return;
      if (!this.galleryObserver) {
        this.galleryObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            entry.target.toggleAttribute('data-av-gallery-visible', entry.isIntersecting);
          });
        }, { threshold: 0.38 });
      }
      this.galleryObserver.observe(root);
    }

    routeDuration(root) {
      const horizon = this.horizonWithin(root);
      const styles = getComputedStyle(horizon || root);
      const value = horizon
        ? styles.getPropertyValue('--au-horizon-duration').trim()
        : styles.getPropertyValue('--au-vg-sequence-duration').trim() || styles.getPropertyValue('--av-trace-route-duration').trim();
      const minimum = horizon ? 160 : 820;
      if (!value) return horizon ? 620 : 820;
      const parsed = value.endsWith('ms') ? Number.parseFloat(value) : Number.parseFloat(value) * 1000;
      return Number.isFinite(parsed) ? Math.max(minimum, parsed) : horizon ? 620 : 820;
    }

    syncTrack(root, forcedProgress = null) {
      if (!root) return;
      const track = root.querySelector('[data-au-track-renderer]');
      if (!track) return;
      const raw = forcedProgress ?? track.dataset.auTrackProgress ?? root.dataset.auTrackProgress ?? 50;
      const progress = Math.min(100, Math.max(0, Number(raw) || 0));
      const guide = track.querySelector('.au-track__route--guide');
      const progressPath = track.querySelector('.au-track__route--progress');
      const marker = track.querySelector('.au-track__marker');
      const halo = track.querySelector('.au-track__marker-halo');
      track.dataset.auTrackProgress = String(progress);
      root.dataset.auTrackProgress = String(progress);
      track.style.setProperty('--au-track-progress', String(progress));
      track.style.setProperty('--au-track-offset', String(100 - progress));
      if (progressPath) {
        progressPath.style.strokeDasharray = '100';
        progressPath.style.strokeDashoffset = String(100 - progress);
      }
      if (!guide || typeof guide.getTotalLength !== 'function') return;
      try {
        const point = guide.getPointAtLength(guide.getTotalLength() * progress / 100);
        [marker, halo].forEach((node) => {
          if (!node) return;
          node.setAttribute('cx', String(point.x));
          node.setAttribute('cy', String(point.y));
        });
      } catch (_) {
        /* Decorative enhancement: keep the no-JS endpoint fallback. */
      }
    }

    setState(root, state, duration = 0) {
      if (!root) return;
      const previousTimer = this.stateTimers.get(root);
      if (previousTimer) window.clearTimeout(previousTimer);
      root.dataset.avTraceState = state;
      const host = root.closest('[data-av-trace-host], .av-trace-host') || root;
      host.dataset.auVgState = state;
      if (duration > 0 && !this.prefersReducedMotion() && this.horizonMotionAllowed(root)) {
        const finalState = state === 'confirming' ? 'confirmed' : 'arrived';
        const timer = window.setTimeout(() => {
          root.dataset.avTraceState = finalState;
          host.dataset.auVgState = finalState;
          this.stateTimers.delete(root);
        }, duration);
        this.stateTimers.set(root, timer);
      }
    }

    handleVariantChange(event) {
      const root = this.resolveRoot(event.target) || document.querySelector('[data-av-trace-surface="pdp-gallery"]');
      if (!root) return;
      this.setState(root, 'switching', this.prefersReducedMotion() ? 0 : 360);
      this.emitAnalytics('living_routes_variant_change', root, {
        variant_id: event.detail?.variant?.id || event.detail?.variantId || null
      });
    }

    handleCartBegin(event) {
      const candidate = event.submitter || event.detail?.button || event.target?.closest?.(PRODUCT_SUBMIT_SELECTOR);
      if (candidate instanceof Element) this.pendingButton = candidate;
      const root = this.resolveRoot(candidate || event.target) || document.querySelector('[data-av-trace-surface="pdp-gallery"]');
      if (root) this.setState(root, 'confirming');
    }

    handleCartSuccess(event) {
      const now = performance.now();
      if (now - this.lastSuccessAt < 650) return;
      this.lastSuccessAt = now;
      const button = event.detail?.button instanceof Element ? event.detail.button : this.pendingButton;
      const root = this.resolveRoot(button || event.target) || document.querySelector('[data-av-trace-surface="pdp-gallery"]');
      if (root) {
        root.dataset.auTrackTerminal = 'true';
        this.syncTrack(root, 100);
        this.setState(root, 'confirming', this.prefersReducedMotion() ? 0 : 420);
      }
      this.emitAnalytics('living_routes_add_to_cart', root, {
        variant_id: event.detail?.variant?.id || event.detail?.variantId || null,
        quantity: event.detail?.quantity || 1
      });
      if (button && document.body?.dataset.avTraceAtcRoute !== 'false') this.drawCartRoute(button, this.findCartTarget());
      this.pendingButton = null;
    }

    handleCartError(event) {
      const button = event.detail?.button instanceof Element ? event.detail.button : this.pendingButton;
      const root = this.resolveRoot(button || event.target) || document.querySelector('[data-av-trace-surface="pdp-gallery"]');
      if (root) this.setState(root, 'error', 2200);
      this.emitAnalytics('living_routes_add_error', root, {
        message: event.detail?.message || event.detail?.error || 'cart_error'
      });
      this.pendingButton = null;
    }

    findCartTarget() {
      return [...document.querySelectorAll(CART_TARGET_SELECTOR)].find((node) => {
        const rect = node.getBoundingClientRect();
        return rect.width > 0 && rect.height > 0;
      }) || null;
    }

    drawCartRoute(button, cartTarget) {
      if (this.prefersReducedMotion() || !button || !cartTarget) return;
      this.removeRouteOverlay();
      const startRect = button.getBoundingClientRect();
      const endRect = cartTarget.getBoundingClientRect();
      const x1 = startRect.left + startRect.width / 2;
      const y1 = startRect.top + startRect.height / 2;
      const x2 = endRect.left + endRect.width / 2;
      const y2 = endRect.top + endRect.height / 2;
      const bend = Math.max(56, Math.abs(x2 - x1) * 0.28);
      const pathData = `M ${x1} ${y1} C ${x1 + bend} ${y1 - 18}, ${x2 - bend} ${y2 + 18}, ${x2} ${y2}`;
      const ns = 'http://www.w3.org/2000/svg';
      const svg = document.createElementNS(ns, 'svg');
      const path = document.createElementNS(ns, 'path');
      const marker = document.createElementNS(ns, 'circle');
      svg.classList.add('av-trace-atc-route');
      svg.setAttribute('aria-hidden', 'true');
      path.setAttribute('d', pathData);
      marker.setAttribute('r', '5');
      marker.setAttribute('cx', String(x2));
      marker.setAttribute('cy', String(y2));
      svg.append(path, marker);
      document.body.append(svg);
      this.routeOverlay = svg;
      const length = path.getTotalLength();
      path.style.strokeDasharray = String(length);
      path.style.strokeDashoffset = String(length);
      path.animate([
        { strokeDashoffset: length, opacity: 0.18 },
        { strokeDashoffset: 0, opacity: 1 }
      ], { duration: 480, easing: 'cubic-bezier(.22,1,.36,1)', fill: 'forwards' });
      marker.animate([
        { opacity: 0, transform: 'scale(.7)', transformOrigin: `${x2}px ${y2}px` },
        { opacity: 1, transform: 'scale(1)', transformOrigin: `${x2}px ${y2}px`, offset: .72 },
        { opacity: 0, transform: 'scale(1)', transformOrigin: `${x2}px ${y2}px` }
      ], { duration: 620, easing: 'cubic-bezier(.22,1,.36,1)', fill: 'forwards' });
      window.setTimeout(() => this.removeRouteOverlay(), 680);
    }

    removeRouteOverlay() {
      this.routeOverlay?.remove();
      this.routeOverlay = null;
    }

    emitView(root) {
      if (!root || this.viewed.has(root)) return;
      if (!this.emitAnalytics('living_routes_view', root)) return;
      this.viewed.add(root);
      const horizon = this.horizonWithin(root);
      if (horizon) this.emitV112('australia:horizon_impression', horizon);
    }

    emitAnalytics(name, root, extra = {}) {
      if (window.AVAnalytics?.consentAllowed?.() !== true) return false;
      const detail = {
        event: name,
        trace_surface: root?.dataset.avTraceSurface || 'unknown',
        trace_world: root?.dataset.avWorld || 'neutral',
        trace_variant: root?.dataset.avTraceVariant || 'master-route',
        trace_density: root?.dataset.avTraceDensity || 'quiet',
        ...extra
      };
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(detail);
      document.dispatchEvent(new CustomEvent('av:trace:analytics', { detail }));
      return true;
    }

    handleV112Click(event) {
      const control = event.target.closest?.('a, button');
      if (!control) return;
      const obsession = control.closest(OBSESSION_SELECTOR);
      if (obsession) {
        this.emitV112('australia:product_obsession_click', obsession, {
          variant: obsession.dataset.avVariant || 'obsession',
          position: obsession.dataset.avPosition || control.dataset.avPosition || '',
          product_id: obsession.dataset.avProductId || control.dataset.avProductId || control.closest('[data-product-id]')?.dataset.productId || ''
        }, 600);
      }
      const proofMethod = control.closest(PROOF_METHOD_SELECTOR);
      if (proofMethod) {
        this.emitV112('australia:proof_method_click', proofMethod, {
          variant: proofMethod.dataset.avVariant || 'proof_method',
          position: proofMethod.dataset.avPosition || ''
        }, 600);
      }
    }

    handleSubmit(event) {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.matches('form[action*="/cart/add"], [data-product-form]')) return;
      const button = event.submitter || form.querySelector(PRODUCT_SUBMIT_SELECTOR);
      if (button instanceof Element) this.pendingButton = button;
      this.handleCartBegin({ target: form, submitter: button, detail: {} });
    }

    handleDocumentEvent(event) {
      if (VARIANT_EVENTS.includes(event.type)) this.handleVariantChange(event);
      else if (BEGIN_EVENTS.includes(event.type)) this.handleCartBegin(event);
      else if (SUCCESS_EVENTS.includes(event.type)) this.handleCartSuccess(event);
      else if (ERROR_EVENTS.includes(event.type)) this.handleCartError(event);
    }

    handleVisibility() {
      document.body?.toggleAttribute('data-av-trace-paused', document.hidden);
      document.body?.toggleAttribute('data-av-horizon-paused', document.hidden);
    }

    handleSectionLoad(event) {
      this.init(event.target);
    }

    handleSectionUnload(event) {
      this.destroy(event.target);
    }

    handleBlockSelect(event) {
      this.rootsWithin(event.target).forEach((root) => this.setState(root, 'arrived'));
    }

    bindEvents() {
      if (this.listenersBound) return;
      this.listenersBound = true;
      document.addEventListener('submit', this.handleSubmit, true);
      document.addEventListener('click', this.handleV112Click);
      [...VARIANT_EVENTS, ...BEGIN_EVENTS, ...SUCCESS_EVENTS, ...ERROR_EVENTS].forEach((name) => {
        document.addEventListener(name, this.handleDocumentEvent);
      });
      document.addEventListener('visibilitychange', this.handleVisibility);
      document.addEventListener('shopify:section:load', this.handleSectionLoad);
      document.addEventListener('shopify:section:unload', this.handleSectionUnload);
      document.addEventListener('shopify:block:select', this.handleBlockSelect);
      document.addEventListener('visitorConsentCollected', () => {
        this.roots.forEach((root) => {
          const rect = root.getBoundingClientRect();
          const visible = rect.bottom > 0 && rect.top < window.innerHeight && rect.right > 0 && rect.left < window.innerWidth;
          if (visible) this.emitView(root);
        });
        this.engagementNodesWithin(document).forEach((node) => {
          const rect = node.getBoundingClientRect();
          const visible = rect.bottom > 0 && rect.top < window.innerHeight && rect.right > 0 && rect.left < window.innerWidth;
          if (visible && node.matches(OBSESSION_SELECTOR)) this.tryObsessionView(node);
        });
      });
    }
  }

  window.AVTraceSystem = AVTraceSystem;
  window.AUVisualGrammar = AVTraceSystem;
  window.avTraceSystem = new AVTraceSystem();
  window.auVisualGrammar = window.avTraceSystem;

  const boot = () => window.avTraceSystem.init(document);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
})();
